<template>
  <div class="ebm-transactions">
    <hs-table-shell
      title="EBM Transactions"
      description="Filter, inspect, and retry store EBM request records."
    >
      <template #filters>
        <div class="ebm-transactions__filters">
          <hs-select
            v-model="filters.status"
            label="Status"
            icon="filter"
            :options="statusOptions"
            @change="applyFilters"
          />
          <hs-select
            v-model="filters.transaction_type"
            label="Transaction Type"
            icon="layers"
            :options="transactionTypeOptions"
            @change="applyFilters"
          />
          <hs-input
            v-model="filters.search"
            label="Search"
            icon="search"
            placeholder="Search UUID or metadata"
            @update:modelValue="debouncedSearch"
          />
          <div class="ebm-transactions__filter-action">
            <hs-button variant="secondary" icon="x-circle" block @click="clearFilters">
              Clear
            </hs-button>
          </div>
        </div>
      </template>

      <div v-if="loading" class="ebm-transactions__loading">
        <hs-table-skeleton :columns="headers.length" :rows="10" />
      </div>

      <hs-alert
        v-else-if="error"
        class="ebm-transactions__alert"
        tone="danger"
        title="Failed to load transactions"
        :message="error?.message || 'Unable to fetch EBM transactions.'"
      />

      <hs-data-table
        v-else-if="transactions && transactions.length > 0"
        :headers="headers"
        :data="transactions"
        :header-key-mapping="headerKeyMapping"
        row-clickable
        @row-click="handleRowClick"
      >
        <template #cell-uuid="{ row }">
          <span class="ebm-transactions__mono" :title="row.uuid">
            {{ truncateUuid(row.uuid) }}
          </span>
        </template>

        <template #cell-invoice="{ row }">
          <hs-badge v-if="getInvoiceNumber(row)" tone="info" size="sm">
            #{{ getInvoiceNumber(row) }}
          </hs-badge>
          <span v-else class="ebm-transactions__muted">-</span>
        </template>

        <template #cell-type="{ row }">
          <hs-badge tone="primary" size="sm">
            {{ formatTransactionType(row.transaction_type) }}
          </hs-badge>
        </template>

        <template #cell-status="{ row }">
          <hs-badge :tone="getStatusTone(row.status)" size="sm">
            {{ row.status || "UNKNOWN" }}
          </hs-badge>
        </template>

        <template #cell-entity="{ row }">
          <div class="ebm-transactions__entity">
            <strong>{{ formatEntityType(row.entity_type) }}</strong>
            <span>#{{ row.entity_id || "N/A" }}</span>
          </div>
        </template>

        <template #cell-failed_at_sequence="{ row }">
          <span v-if="row.failed_at_sequence" class="ebm-transactions__danger">
            Step {{ row.failed_at_sequence }}
          </span>
          <span v-else class="ebm-transactions__muted">-</span>
        </template>

        <template #cell-retry_count="{ row }">
          {{ row.retry_count || 0 }}
        </template>

        <template #cell-is_retriable="{ row }">
          <div class="ebm-transactions__stack">
            <hs-badge :tone="row.is_retriable ? 'success' : 'danger'" size="sm">
              {{ row.is_retriable ? "Yes" : "No" }}
            </hs-badge>
            <span
              v-if="row.status === 'COMPLETED' && (!row.checkpoints || row.checkpoints.length === 0)"
              class="ebm-transactions__muted"
            >
              No checkpoints
            </span>
          </div>
        </template>

        <template #cell-next_retry_at="{ row }">
          {{ row.next_retry_at ? formatDate(row.next_retry_at) : "-" }}
        </template>

        <template #cell-created_at="{ row }">
          {{ formatDate(row.created_at) }}
        </template>

        <template #cell-actions="{ row }">
          <hs-button variant="secondary" icon="eye" size="sm" @click.stop="handleViewDetails(row)">
            Details
          </hs-button>
        </template>
      </hs-data-table>

      <hs-empty-state
        v-else
        icon="inbox"
        title="No EBM transactions found"
        :description="hasActiveFilters ? 'Try adjusting your filters or search terms.' : 'Transactions will appear here once EBM requests are recorded.'"
      />

      <template #footer>
        <hs-pagination
          :current-page="pagination.currentPage"
          :total-pages="pagination.totalPages"
          :total-items="pagination.totalItems"
          :items-per-page="pagination.itemsPerPage"
          @previous-page="$emit('page-change', pagination.currentPage - 1)"
          @next-page="$emit('page-change', pagination.currentPage + 1)"
          @update:itemsPerPage="$emit('items-per-page-change', $event)"
        />
      </template>
    </hs-table-shell>
  </div>
</template>

<script>
import { format } from "date-fns";
import { debounce } from "lodash";
import {
  HsAlert,
  HsBadge,
  HsButton,
  HsDataTable,
  HsEmptyState,
  HsInput,
  HsPagination,
  HsSelect,
  HsTableShell,
} from "@/components/ui";
import HsTableSkeleton from "@/components/table/HSTableSkeleton.vue";

export default {
  name: "EbmTransactionsTable",
  components: {
    HsAlert,
    HsBadge,
    HsButton,
    HsDataTable,
    HsEmptyState,
    HsInput,
    HsPagination,
    HsSelect,
    HsTableShell,
    HsTableSkeleton,
  },
  props: {
    transactions: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: [Object, String],
      default: null,
    },
    pagination: {
      type: Object,
      default: () => ({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
      }),
    },
  },
  emits: [
    "filter-change",
    "row-click",
    "view-details",
    "page-change",
    "items-per-page-change",
  ],
  data() {
    return {
      filters: {
        status: "",
        transaction_type: "",
        search: "",
      },
      headers: [
        "UUID",
        "Invoice",
        "Type",
        "Status",
        "Entity",
        "Failed At",
        "Retry Count",
        "Retriable",
        "Next Retry",
        "Created At",
        "Actions",
      ],
      headerKeyMapping: {
        UUID: "uuid",
        Invoice: "invoice",
        Type: "transaction_type",
        Status: "status",
        Entity: "entity_info",
        "Failed At": "failed_at_sequence",
        "Retry Count": "retry_count",
        Retriable: "is_retriable",
        "Next Retry": "next_retry_at",
        "Created At": "created_at",
        Actions: "actions",
      },
      statusOptions: [
        { value: "", label: "All Statuses" },
        { value: "FAILED", label: "Failed" },
        { value: "PARTIALLY_COMPLETED", label: "Partially Completed" },
        { value: "PENDING", label: "Pending" },
        { value: "IN_PROGRESS", label: "In Progress" },
        { value: "COMPLETED", label: "Completed" },
      ],
      transactionTypeOptions: [
        { value: "", label: "All Types" },
        { value: "SALE", label: "Sale" },
        { value: "PURCHASE", label: "Purchase" },
        { value: "PRODUCT_REGISTRATION", label: "Product Registration" },
        { value: "SERVICE_REGISTRATION", label: "Service Registration" },
        { value: "IMPORT", label: "Import" },
        { value: "STOCK_UPDATE", label: "Stock Update" },
        { value: "CUSTOMER_REGISTRATION", label: "Customer Registration" },
      ],
      debouncedSearch: null,
    };
  },
  computed: {
    hasActiveFilters() {
      return (
        this.filters.status ||
        this.filters.transaction_type ||
        this.filters.search
      );
    },
  },
  created() {
    this.debouncedSearch = debounce(this.applyFilters, 500);
  },
  methods: {
    truncateUuid(uuid) {
      if (!uuid) return "N/A";
      return uuid.length > 12 ? `${uuid.substring(0, 8)}...` : uuid;
    },
    formatDate(dateString) {
      if (!dateString) return "N/A";
      try {
        return format(new Date(dateString), "MMM dd, HH:mm");
      } catch (error) {
        return "Invalid date";
      }
    },
    formatTransactionType(type) {
      if (!type) return "Unknown";
      return String(type)
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    },
    formatEntityType(type) {
      if (!type) return "Unknown";
      return String(type)
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    },
    getStatusTone(status) {
      const tones = {
        FAILED: "danger",
        PARTIALLY_COMPLETED: "warning",
        PENDING: "warning",
        IN_PROGRESS: "info",
        COMPLETED: "success",
      };
      return tones[status] || "neutral";
    },
    applyFilters() {
      this.$emit("filter-change", { ...this.filters });
    },
    clearFilters() {
      this.filters = {
        status: "",
        transaction_type: "",
        search: "",
      };
      this.applyFilters();
    },
    getInvoiceNumber(transaction) {
      if (!transaction?.checkpoints) return null;
      const saleSaveLog = transaction.checkpoints.find(
        (checkpoint) => checkpoint.checkpoint_type === "SALE_SAVE"
      );
      return saleSaveLog?.input_payload?.invcNo || null;
    },
    handleRowClick(row) {
      this.$emit("row-click", row);
    },
    handleViewDetails(row) {
      this.$emit("view-details", row);
    },
  },
};
</script>

<style scoped>
.ebm-transactions {
  min-width: 0;
}

.ebm-transactions__filters {
  display: grid;
  gap: 0.85rem;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.ebm-transactions__filter-action {
  align-self: end;
}

.ebm-transactions__loading {
  overflow-x: auto;
  padding: 0.85rem;
}

.ebm-transactions__alert {
  margin: 0.85rem;
}

.ebm-transactions__mono {
  color: var(--hs-text, #0f172a);
  cursor: pointer;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  font-size: 0.82rem;
  font-weight: 800;
}

.ebm-transactions__muted {
  color: var(--hs-muted, #64748b);
  font-size: 0.76rem;
  font-weight: 750;
}

.ebm-transactions__danger {
  color: #dc2626;
  font-size: 0.82rem;
  font-weight: 850;
}

.ebm-transactions__entity,
.ebm-transactions__stack {
  display: grid;
  gap: 0.18rem;
}

.ebm-transactions__entity strong {
  color: var(--hs-text, #0f172a);
  font-size: 0.83rem;
  font-weight: 850;
}

.ebm-transactions__entity span {
  color: var(--hs-muted, #64748b);
  font-size: 0.75rem;
}

@media (max-width: 980px) {
  .ebm-transactions__filters {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 620px) {
  .ebm-transactions__filters {
    grid-template-columns: 1fr;
  }
}
</style>
